/**
 * BundleManager — singleton wrapping Shopify Cart API.
 *
 * localStorage stores ONLY the target bundle size (5 or 10).
 * All item state lives in the real Shopify cart.
 *
 * Dispatches 'cart:updated' on document after every cart mutation,
 * with event.detail = cart JSON from Shopify.
 */
(function () {
  'use strict';

  var SIZE_KEY = 'curate_collect_target_size';
  var GIFT_CARD_HANDLE = 'v1-rise-ai-giftcard';

  function isGiftCardOnly(cart) {
    if (!cart || !cart.items || cart.items.length === 0) return false;
    return cart.items.every(function (item) {
      return item.handle === GIFT_CARD_HANDLE;
    });
  }

  function setLoading(isLoading) {
    document.dispatchEvent(new CustomEvent('cart:loading', { detail: isLoading }));
  }

  function notify(cart) {
    setLoading(false);
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
    if (typeof updateCartBadge === 'function') updateCartBadge();
  }

  function formatMoney(cents) {
    var amount = (cents / 100).toFixed(2);
    var parts = amount.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return '$' + parts.join('.');
  }

  window.BundleManager = {

    formatMoney: formatMoney,

    // --- Target size (localStorage only) ---

    getTargetSize: function () {
      var val = localStorage.getItem(SIZE_KEY);
      var n = parseInt(val, 10);
      return (n === 5 || n === 10) ? n : null;
    },

    setTargetSize: function (size) {
      size = parseInt(size, 10);
      if (size !== 5 && size !== 10) return Promise.resolve();

      var prev = this.getTargetSize();
      localStorage.setItem(SIZE_KEY, size);

      // Clear cart if size changed
      if (prev !== null && prev !== size) {
        return this.clearCart();
      }
      // If first time setting, also clear to start fresh
      if (prev === null) {
        return this.clearCart();
      }
      // Same size: keep bundle items, but strip out any gift cards
      // (bundle flow requires exactly 5/10 bundle items — gift cards don't belong here).
      return this.removeGiftCards();
    },

    removeGiftCards: function () {
      setLoading(true);
      return window.BundleManager.getCart().then(function (cart) {
        if (!cart || !cart.items || cart.items.length === 0) {
          setLoading(false);
          return cart;
        }
        var updates = {};
        var hasGiftCard = false;
        cart.items.forEach(function (item) {
          if (item.handle === GIFT_CARD_HANDLE) {
            updates[item.key] = 0;
            hasGiftCard = true;
          }
        });
        if (!hasGiftCard) {
          setLoading(false);
          notify(cart);
          return cart;
        }
        return fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ updates: updates })
        })
          .then(function () { return window.BundleManager.getCart(); })
          .then(function (cart) { notify(cart); return cart; });
      });
    },


    // --- Shopify Cart API wrappers ---

    getCart: function () {
      return fetch('/cart.js', {
        headers: { 'Content-Type': 'application/json' }
      }).then(function (r) { return r.json(); });
    },

    addItem: function (variantId, quantity) {
      quantity = quantity || 1;
      setLoading(true);
      return fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [{ id: parseInt(variantId, 10), quantity: quantity }]
        })
      })
        .then(function () {
          return window.BundleManager.getCart();
        })
        .then(function (cart) {
          notify(cart);
          return cart;
        });
    },

    addItems: function (items) {
      // items = [{ id: variantId, quantity: qty }, ...]
      setLoading(true);
      return fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: items })
      })
        .then(function () {
          return window.BundleManager.getCart();
        })
        .then(function (cart) {
          notify(cart);
          return cart;
        });
    },

    removeItem: function (lineKey) {
      setLoading(true);
      return fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: lineKey, quantity: 0 })
      })
        .then(function () {
          return window.BundleManager.getCart();
        })
        .then(function (cart) {
          notify(cart);
          return cart;
        });
    },

    updateItem: function (lineKey, quantity) {
      setLoading(true);
      return fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: lineKey, quantity: quantity })
      })
        .then(function () {
          return window.BundleManager.getCart();
        })
        .then(function (cart) {
          notify(cart);
          return cart;
        });
    },

    clearCart: function () {
      setLoading(true);
      return fetch('/cart/clear.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      })
        .then(function () {
          return window.BundleManager.getCart();
        })
        .then(function (cart) {
          notify(cart);
          return cart;
        });
    },

    // --- Validation ---

    canCheckout: function (cart) {
      if (!cart) return false;
      if (isGiftCardOnly(cart)) return true;
      var target = this.getTargetSize();
      if (!target) return false;
      return cart.item_count === target;
    },

    isFull: function (cart) {
      var target = this.getTargetSize();
      if (!target || !cart) return false;
      return cart.item_count >= target;
    },

    isGiftCardOnly: isGiftCardOnly
  };

  // Fire initial event on load
  document.addEventListener('DOMContentLoaded', function () {
    window.BundleManager.getCart().then(notify);
  });
})();
